/**
 * Dos avisos de "lo que falta" que comparten varios demos:
 *  a) Complementarios que faltan: conceptos principales que están en la hoja sin lo que normalmente los
 *     acompaña (aplanado, pintura, cimbra…).
 *  b) Lo que no trae el modelo: instalaciones que implican los CUARTOS del modelo (IfcSpace).
 * Los cuartos se leen del modelo; qué acompaña a qué y qué instalación lleva cada cuarto es de ejemplo.
 */
import type { ModeloDemo } from './modeloDemo.js';
import { HP_EJEMPLO } from './logicaDemo.js';
import { NotaEjemplo, Panel } from './DemoMarco.js';
import datos from './datos/lo-que-falta.json';

const COMPLEMENTOS = datos.complementos as Record<string, { id: string; texto: string }[]>;

function lista(xs: string[]): string {
  if (xs.length <= 1) return xs.join('');
  return `${xs.slice(0, -1).join(', ')} ni ${xs[xs.length - 1]}`;
}

export function LoQueFalta({
  modelo,
  conceptosEnHoja,
  yaIncluidos = [],
}: {
  modelo: ModeloDemo | null;
  /** Ids de los conceptos principales que están en la hoja. */
  conceptosEnHoja: string[];
  /** Complementos que la hoja ya trae (por id), para no pedirlos otra vez. */
  yaIncluidos?: string[];
}) {
  const complementarios = conceptosEnHoja
    .map((id) => {
      const c = HP_EJEMPLO.conceptos.find((x) => x.id === id);
      const faltan = (COMPLEMENTOS[id] ?? []).filter((k) => !yaIncluidos.includes(k.id));
      return c && faltan.length ? { c, faltan } : null;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);

  const cuartos = new Map<string, { cuarto: string; faltan: string[]; veces: number }>();
  for (const nm of modelo?.espacios ?? []) {
    const r = datos.cuartos.find((q) => q.nombresEnModelo.some((n) => n.toLowerCase() === nm.toLowerCase()));
    if (!r) continue;
    const x = cuartos.get(r.cuarto) ?? cuartos.set(r.cuarto, { cuarto: r.cuarto, faltan: r.faltan, veces: 0 }).get(r.cuarto)!;
    x.veces++;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14, marginTop: 14 }}>
      <Panel titulo="Complementarios que faltan">
        {complementarios.length ? (
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 'var(--fs-300)', lineHeight: 1.5 }}>
            {complementarios.map(({ c, faltan }) => (
              <li key={c.id}>
                <b style={{ color: 'var(--text-primary)' }}>{c.descripcion}</b>: está en tu hoja, pero no {lista(faltan.map((f) => f.texto))}.
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: 'var(--text-secondary)' }}>No encontramos complementarios faltantes.</div>
        )}
        <NotaEjemplo>Qué acompaña a cada concepto es de ejemplo.</NotaEjemplo>
      </Panel>
      <Panel titulo="Lo que no trae el modelo">
        {cuartos.size ? (
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 'var(--fs-300)', lineHeight: 1.5 }}>
            {[...cuartos.values()].map((x) => (
              <li key={x.cuarto}>
                <b style={{ color: 'var(--text-primary)' }}>
                  {x.cuarto}
                  {x.veces > 1 ? ` (${x.veces})` : ''}
                </b>
                : el modelo no trae {lista(x.faltan)}.
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ color: 'var(--text-secondary)' }}>El modelo no trae nombres de cuartos: no podemos deducir sus instalaciones.</div>
        )}
        <NotaEjemplo>
          Los cuartos se leen del modelo ({modelo?.espacios.length ?? 0} espacios). Qué instalación lleva cada cuarto es de ejemplo. {datos.aviso}
        </NotaEjemplo>
      </Panel>
    </div>
  );
}
